/*eslint max-lines-per-function: ["error", 165]*/
import React, { ReactNode } from 'react';
import { Alert, Button, Table } from 'react-bootstrap';

import {
  CreateUpdatePurchaseInvoiceEstimateItemDto,
  GetEstimateItemDto,
  GetPurchaseInvoiceEstimateItemDto,
  GetPurchaseInvoiceExtendedDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { useTranslation } from '../../../common/hooks/useTranslation';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { usePokCore } from '../../../common/hooks/usePokCore';
import momencik from '../../../common/momencik';
import { useNavigation } from '../../../common/navigation';
import numberFormatter from '../../../common/numberFormatter';
import mathUtils from '../../../utils/mathUtils';
import CurrencyControl from '../../../app/components/CurrencyControl';
import { CopyAllButton, CopySingleButton } from '../Invoices/CopyAmountButtons';

import {
  getNotAssignedAmount,
  SumData,
} from './purchaseInvoicePositionsGridUtils';
import { PurchaseInvoiceEstimateItemPositionsGridSum } from './PurchaseInvoiceEstimateItemPositionsGridSum';
import { PurchaseInvoiceEstimateItemPositionsGridRow } from './PurchaseInvoiceEstimateItemPositionsGridRow';
import { PurchaseNetTotalForm } from './PurchaseNetTotalForm';

interface PurchaseInvoiceEstimateItemPositionsGridProps {
  extendedPurchaseInvoice?: GetPurchaseInvoiceExtendedDto;
  purchaseInvoiceEstimateItems: CreateUpdatePurchaseInvoiceEstimateItemDto[];
  availableEstimateItems: GetEstimateItemDto[];
  readOnly: boolean;
  handleRefresh: () => void;
  handleAmountChange: (
    estimateItemId: string,
    projectId: string | undefined,
    item: CreateUpdatePurchaseInvoiceEstimateItemDto | undefined,
    amount: string | null | undefined,
  ) => void;
  warnings: string[];
  sumData: SumData;
  handleFillAllAmounts: () => void;
}

export interface TableColumn {
  header: ReactNode;
  isHidden?: boolean;
  tdCssClass?: string;
  renderCell: (
    estimateItem: GetEstimateItemDto,
    purchaseInvoiceEstimateItem: CreateUpdatePurchaseInvoiceEstimateItemDto,
    savedItem?: GetPurchaseInvoiceEstimateItemDto,
  ) => string | ReactNode | null;
  onlyTV?: boolean;
}

const TableHead = ({ columns }: { columns: TableColumn[] }) => (
  <thead>
    <tr>
      {columns
        .filter(({ isHidden }) => !isHidden)
        .map((col, index) => (
          <th key={`${col.header}-${index}`}>{col.header}</th>
        ))}
    </tr>
  </thead>
);

export const PurchaseInvoiceEstimateItemPositionsGrid: React.FC<
  PurchaseInvoiceEstimateItemPositionsGridProps
> = props => {
  const { t, tk } = useTranslation('fvzLng');
  const pok = usePokCore();
  const nav = useNavigation();

  const columns: TableColumn[] = [
    {
      header: t(tk.prLang.estimateItemPosition),
      renderCell: (estimateItem, purchaseInvoiceEstimateItem, savedItem) => (
        <>
          {estimateItem.position.name}
          {savedItem ? (
            <LastEditorInfo
              className="last-editor-inline"
              id={savedItem.id}
              method={pok.purchaseInvoices.getLastEstimateItemEditor}
            />
          ) : null}
        </>
      ),
    },
    {
      header: t(tk.common.month),
      renderCell: estimateItem =>
        momencik(estimateItem.estimateByMonth.date, 'YYYY-MM'),
    },
    {
      header: t(tk.prLang.project),
      renderCell: estimateItem => (
        <>
          {estimateItem.estimateByMonth.project.name}
          {' ('}
          <Button
            variant="link"
            className="p-0 m-0"
            onClick={() =>
              nav.projectDetails(
                estimateItem.estimateByMonth.project.id,
                true,
                'estimates',
              )
            }
          >
            {estimateItem.estimateByMonth.project.number}
          </Button>
          {')'}
        </>
      ),
    },
    {
      header: t(tk.finance.orderNumber),
      renderCell: estimateItem => estimateItem.orderNumber,
      isHidden: !props.extendedPurchaseInvoice?.tv,
    },
    {
      header: t(tk.common.budget),
      renderCell: estimateItem =>
        estimateItem.estimateByMonth.project.purchaser?.budget?.name,
    },
    {
      header: t(tk.finance.purchaseNetTotal),
      renderCell: estimateItem => (
        <PurchaseNetTotalForm
          estimateItem={estimateItem}
          purchaseInvoiceId={props.extendedPurchaseInvoice?.id}
          handleRefresh={props.handleRefresh}
          disabled={props.readOnly}
        />
      ),
      tdCssClass: 'amounts-align-right',
    },
    {
      header: t(tk.finance.notAssignedAmount),
      renderCell: estimateItem =>
        numberFormatter(getNotAssignedAmount(estimateItem), true),
      tdCssClass: 'amounts-align-right',
    },
    {
      header: (
        <CopyAllButton
          allFilled={false}
          onClick={props.handleFillAllAmounts}
          disabled={props.readOnly}
          tooltip={t(tk.finance.fillAllFields)}
        />
      ),
      renderCell: (estimateItem, purchaseInvoiceEstimateItem, savedItem) => (
        <CopySingleButton
          hidden={false}
          onClick={() =>
            props.handleAmountChange(
              estimateItem.id,
              estimateItem?.estimateByMonth?.project?.id,
              purchaseInvoiceEstimateItem,
              mathUtils
                .round(
                  mathUtils.add(
                    getNotAssignedAmount(estimateItem),
                    savedItem?.amount || '0',
                  ),
                )
                .toString(),
            )
          }
          disabled={props.readOnly}
          tooltip={t(tk.finance.fillNotAssignedAmount)}
        />
      ),
    },
    {
      header: t(tk.common.amount),
      renderCell: (estimateItem, purchaseInvoiceEstimateItem) => (
        <CurrencyControl
          value={purchaseInvoiceEstimateItem?.amount}
          decimalScale={2}
          disabled={props.readOnly}
          onChange={value =>
            props.handleAmountChange(
              estimateItem.id,
              estimateItem?.estimateByMonth?.project?.id,
              purchaseInvoiceEstimateItem,
              value,
            )
          }
          allowNegative
        />
      ),
    },
  ];

  return (
    <>
      <Table striped>
        <TableHead columns={columns} />
        <tbody>
          {props.availableEstimateItems.map(estimateItem => (
            <PurchaseInvoiceEstimateItemPositionsGridRow
              {...props}
              key={estimateItem.id}
              estimateItem={estimateItem}
              columns={columns}
            />
          ))}
          <PurchaseInvoiceEstimateItemPositionsGridSum {...props} />
        </tbody>
      </Table>
      {!!props.warnings.length && (
        <Alert variant="danger">{props.warnings}</Alert>
      )}
    </>
  );
};
